import { useEffect, useId, useRef, useState } from 'react';
import { ArrowRight, Check, Plus } from 'lucide-react';

/* ── Kinetic statement: words light up as the reader scrolls through ── */
export function ScrollStatement({ text, accent = [], reduced }) {
  const ref = useRef(null);
  const words = text.split(' ');
  const [lit, setLit] = useState(reduced ? words.length : 0);

  useEffect(() => {
    if (reduced || !('IntersectionObserver' in window)) { setLit(words.length); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    let raf = 0;
    let active = false;
    const update = () => {
      raf = 0;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the block's top reaches 85% of the viewport, 1 when its bottom reaches 45%
      const p = Math.min(1, Math.max(0, (vh * 0.85 - r.top) / (vh * 0.85 - vh * 0.45 + r.height)));
      setLit(Math.round(p * words.length));
    };
    const onScroll = () => { if (active && !raf) raf = requestAnimationFrame(update); };
    const io = new IntersectionObserver(([e]) => { active = e.isIntersecting; if (active) update(); });
    io.observe(el);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => { io.disconnect(); window.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, [reduced, words.length]);

  return (
    <p ref={ref} className="a9l-statement" data-testid="scroll-statement">
      {words.map((w, i) => (
        <span key={i}>
          <span className={`w ${i < lit ? 'on' : ''} ${accent.includes(i) ? 'accent' : ''}`}>{w}</span>{' '}
        </span>
      ))}
    </p>
  );
}

/* ── One call, four moves: sticky stage + chapters that drive it ── */
export function StoryChapters({ chapters, panels }) {
  const [active, setActive] = useState(0);
  const refs = useRef([]);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(Number(e.target.dataset.index)); }),
      { rootMargin: '-45% 0px -45% 0px' }
    );
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="a9l-story" data-testid="how-it-works-grid">
      <div className="a9l-story__stage" aria-hidden="true">
        {panels.map((p, i) => (
          <div key={i} className={`a9l-panel ${i === active ? 'is-active' : ''}`}>
            <div className="flex items-start justify-between">
              <span className="a9l-label a9l-label--signal">{chapters[i].label}</span>
              <span className="a9l-panel__num">{chapters[i].num}</span>
            </div>
            <div className="a9l-panel__visual">{p}</div>
            <p className="a9l-panel__cap a9l-label">{chapters[i].caption}</p>
          </div>
        ))}
      </div>
      <ol className="a9l-chapters">
        {chapters.map((c, i) => (
          <li
            key={c.num}
            ref={(el) => { refs.current[i] = el; }}
            data-index={i}
            data-testid={`how-step-${c.num}`}
            className={`a9l-chapter ${i === active ? 'is-active' : ''}`}
          >
            <span className="a9l-label a9l-kicker a9l-label--signal">{c.num} · {c.label}</span>
            <h3 className="a9l-chapter__title">{c.title}</h3>
            <p className="a9l-body a9l-chapter__body">{c.body}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ── Industries: accessible tabs (arrow keys, Home/End) ── */
export function IndustryTabs({ items, onTry }) {
  const [sel, setSel] = useState(0);
  const tabRefs = useRef([]);
  const base = useId();

  const onKey = (e) => {
    const n = items.length;
    let next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (sel + 1) % n;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (sel - 1 + n) % n;
    if (e.key === 'Home') next = 0;
    if (e.key === 'End') next = n - 1;
    if (next !== null) { e.preventDefault(); setSel(next); tabRefs.current[next]?.focus(); }
  };

  const it = items[sel];
  return (
    <div>
      <div role="tablist" aria-label="Industries" className="a9l-tabs" onKeyDown={onKey}>
        {items.map((x, i) => (
          <button
            key={x.key}
            ref={(el) => { tabRefs.current[i] = el; }}
            role="tab"
            type="button"
            id={`${base}-tab-${i}`}
            aria-selected={i === sel}
            aria-controls={`${base}-panel`}
            tabIndex={i === sel ? 0 : -1}
            className="a9l-tab"
            data-testid={`industry-tab-${x.key}`}
            onClick={() => setSel(i)}
          >
            {x.tab}
          </button>
        ))}
      </div>
      <div role="tabpanel" id={`${base}-panel`} aria-labelledby={`${base}-tab-${sel}`} className="a9l-ind" data-testid={`industry-panel-${it.key}`}>
        <div>
          <h3 className="a9l-ind__title">{it.title}</h3>
          <p className="a9l-body mt-4 max-w-xl">{it.desc}</p>
          <ul className="a9l-ind__list">
            {it.points.map((p) => (
              <li key={p}><Check size={16} aria-hidden="true" /><span>{p}</span></li>
            ))}
          </ul>
          <button type="button" className="a9l-btn a9l-btn--ghost mt-8" data-testid={`industry-try-${it.key}`} onClick={() => onTry(`industry-${it.key}`)}>
            Hear how it sounds <ArrowRight size={16} className="a9l-arrow" aria-hidden="true" />
          </button>
        </div>
        <div className="a9l-convo" aria-label={`Example ${it.tab.toLowerCase()} call`}>
          <span className="a9l-label">Example call · {it.tab}</span>
          {it.convo.map(([who, text], i) => (
            <div key={i} className={`a9l-msg a9l-msg--${who}`}>
              <span className="a9l-msg__who">{who === 'agent' ? 'Agent9' : 'Caller'}</span>
              <span className="a9l-msg__text">{text}</span>
            </div>
          ))}
          <div className="a9l-call__outcomes">
            {it.outcomes.map((o) => (
              <span key={o} className="a9l-outcome"><Check size={12} aria-hidden="true" /> {o}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── FAQ: buttons with aria-expanded, height animated via grid rows ── */
export function FaqList({ items }) {
  const [open, setOpen] = useState(-1);
  const base = useId();
  return (
    <div className="a9l-faq">
      {items.map((f, i) => {
        const isOpen = open === i;
        return (
          <div key={f.q} className="a9l-faq__item">
            <h3>
              <button
                type="button"
                id={`${base}-q-${i}`}
                className="a9l-faq__q"
                aria-expanded={isOpen}
                aria-controls={`${base}-a-${i}`}
                data-testid={`faq-${f.q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
                onClick={() => setOpen(isOpen ? -1 : i)}
              >
                <span>{f.q}</span>
                <span className="a9l-faq__icon" aria-hidden="true"><Plus size={14} /></span>
              </button>
            </h3>
            <div id={`${base}-a-${i}`} role="region" aria-labelledby={`${base}-q-${i}`} className={`a9l-faq__a ${isOpen ? 'is-open' : ''}`} aria-hidden={!isOpen}>
              <div><p className="a9l-body pb-6 pr-10 max-w-3xl">{f.a}</p></div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
